import AppRoutes from "../routes/Routes";
import Header from "../components/Header/Header";
import { useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import Footer from "../components/Footer/Footer";
import { RBACProvider, useRBAC } from "@impelsysinc/react-rbac";
import Alert from "../components/Alert/Alert";
import './../components/Header/Header.scss';
import { useDispatch, useSelector } from "react-redux";
import { RootState } from '../store/store';
import { CryptoMethods } from "../utils/crypto-methods";
import {
  getEncryptionConfig,
  getTermsAndConditionStatus,
  getTokenPlayload,
  setTermsAndConditionStatus,
  ssoLogout,
  updateTermsAndConditionAcceptance,
} from "../services/auth.service";
import { updateUser } from "../store/userSlice";
import TermsAndConditionsModal from "../components/TermsAndConditionsModal/TermsAndConditionsModal";
import { showAlert } from "../store/alertSlice";


const App = () => {
  const dispatch = useDispatch();
  const [isCreateEncryptKey, setIsCreateEncryptKey] = useState(true);
  const { visible } = useSelector(
    (state: RootState) => state.alert
  );
  const rbac = useRBAC({
    permissions: JSON.parse(localStorage.getItem("uiPermissions") ?? "[]"),
  });
  const location = useLocation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [isTermsActionLoading, setIsTermsActionLoading] = useState(false);

  const populateLoggedInUserOnPageLoad = async () => {
    const user = getTokenPlayload();
    if (user) {
      const firstName = await CryptoMethods.ecbDecryptData(user.user_firstName);
      const lastName = await CryptoMethods.ecbDecryptData(user.user_lastName);
      const email = await CryptoMethods.ecbDecryptData(user.user_email);
      dispatch(updateUser({ firstName, lastName, email}));
    }
  };

  useEffect(() => {
    async function init() {
      let encryptionConfig: any = null;
      const configFromSession = sessionStorage.getItem('encryptionConfig');
      if (configFromSession) {
        encryptionConfig = JSON.parse(configFromSession);
      } else {
        encryptionConfig = await getEncryptionConfig();
        sessionStorage.setItem('encryptionConfig', JSON.stringify(encryptionConfig));
      }
      await CryptoMethods.setCryptoKey(encryptionConfig);
      setIsCreateEncryptKey(false);
      setIsLoggedIn(true);
      populateLoggedInUserOnPageLoad();
    }
    init();
  }, []);

  useEffect(() => {
    const shouldShowTermsModal =
      location.pathname !== "/signin-success" &&
      location.pathname !== "/terms-of-use" &&
      Boolean(getTokenPlayload()) &&
      getTermsAndConditionStatus() === false;

    setShowTermsModal(shouldShowTermsModal);
  }, [location.pathname]);

  const handleTermsAccept = async () => {
    try {
      setIsTermsActionLoading(true);
      const termsAndConditionId = localStorage.getItem("termsAndConditionId") ?? null;
      
      if (!termsAndConditionId) {
        throw new Error("Missing termsAndConditionId");
      }

      const response = await updateTermsAndConditionAcceptance(termsAndConditionId);
      
      if (response.success) {
        setTermsAndConditionStatus(true);
        setShowTermsModal(false);
      } else {
        dispatch(showAlert({
          message: "Failed to accept terms and conditions",
          type: "error",
        }));
        throw new Error(response.error || "Failed to accept terms");
      }
    } catch (error) {
      console.error("Error accepting terms:", error);
      // Don't update status on error
    } finally {
      setIsTermsActionLoading(false);
    }
  };

  const handleTermsDecline = async () => {
    setShowTermsModal(false);
    await ssoLogout();
  };
  

  return (
    <>
      {!isCreateEncryptKey && (
      <RBACProvider rbac={rbac}>
      {location.pathname !== "/signin-success" && (
        <>
        {visible &&
          <div className="alert-message">
            <Alert />
          </div>
        }
        <Header showProfile={isLoggedIn && location.pathname !== "/" && location.pathname !== "/user-consent"} />
      </>
      )}
      <div className="main-wrapper" id="main" tabIndex={-1}>
        <AppRoutes />
        <Footer />
      </div>
      <TermsAndConditionsModal
        isOpen={showTermsModal}
        onAccept={handleTermsAccept}
        onDecline={handleTermsDecline}
        loading={isTermsActionLoading}
      />
    </RBACProvider>
    )}
    </>
  );
};

export default App;
